import moment from 'moment';
import React, { ReactNode } from 'react';
import styled, { CSSProp, StyledComponentPropsWithRef } from 'styled-components';
import DateTime from './DateTime';

type Props = {
  dateRange?: { from?: moment.Moment | null; to?: moment.Moment | null };
  setDateRange?: (dateRange: { from?: moment.Moment | null; to?: moment.Moment | null }) => void;
  validTypeFrom?: 'invalidAfterToday' | 'none';
  validTypeTo?: 'invalidAfterToday' | 'invalidFromBeforeDay' | 'none';
  wrapperStyles?: CSSProp;
  dateTimeProps?: {
    toProps?: ViewDate;
    fromProps?: ViewDate;
  };
  className?: string;
  seperatorProps?: {
    seperator?: ReactNode;
    styles?: CSSProp;
  };
};

type ViewDate = Omit<StyledComponentPropsWithRef<typeof DateTime>, 'locale' | 'onChange' | 'value'>;

const DatetimeWrapper = styled.div<{ styles?: CSSProp }>`
  display: flex;
  margin-bottom: 10px;
  align-items: center;
  gap: 4px;
  ${(props) => props.styles}

  #dateRangeFrom:read-only, #dateRangeTo:read-only {
    background-color: inherit;
  }
  #dateRangeFrom:disabled,
  #dateRangeTo:disabled {
    background-color: #eee;
  }
  ${DateTime} {
    margin-bottom: 0;
  }
`;

const Seperator = styled.span<{ styles?: CSSProp }>`
  ${(props) => props.styles}
`;

const DateTimeRange = ({
  dateRange = {},
  setDateRange,
  validTypeTo,
  validTypeFrom,
  wrapperStyles,
  className,
  dateTimeProps = {},
  seperatorProps = {},
}: Props) => {
  const today = moment().locale('ja-JP');

  const isInvalidAfterToday = (date: moment.Moment) => !date.isAfter(today);

  const isInvalidFromBeforeDay = (date: moment.Moment) => {
    if (dateRange.from !== null && typeof dateRange.from === 'object') {
      return !date.isBefore(dateRange.from);
    }
    return false;
  };

  // viewDate is used for setting the default time to 23:59:59 on open calendar
  // but viewDate overwrites the locale prop with the moment object passed into it
  // so we need to explicitly re-set the locale back to jp again
  const validViewDate = () => {
    const toViewDate = moment(dateRange?.to);

    if (!toViewDate.isValid()) {
      return today.endOf('day');
    }

    return toViewDate.locale('ja-JP');
  };

  const { toProps, fromProps } = dateTimeProps;
  const { inputProps: toInputProps, ...restToProps } = toProps ?? {};
  const { inputProps: fromInputProps, ...restFromProps } = fromProps ?? {};

  return (
    <DatetimeWrapper styles={wrapperStyles} className={className}>
      <DateTime
        inputProps={{
          id: 'dateRangeFrom',
          name: 'dateRangeFrom',
          ...fromInputProps,
        }}
        locale="ja-JP"
        onChange={(from) => {
          if (typeof from === 'object') {
            setDateRange?.({ ...dateRange, from });
          } else {
            setDateRange?.({ ...dateRange, from: null });
          }
        }}
        value={dateRange.from ?? undefined}
        isValidDate={(date) => {
          if (validTypeFrom === 'invalidAfterToday') {
            return isInvalidAfterToday(date);
          }
          return true;
        }}
        timeFormat="HH:mm:ss"
        {...restFromProps}
      />
      <Seperator styles={seperatorProps.styles}>{seperatorProps.seperator ?? '〜'}</Seperator>
      <DateTime
        inputProps={{ id: 'dateRangeTo', name: 'dateRangeTo', ...toInputProps }}
        locale="ja-JP"
        onChange={(to) => {
          if (typeof to === 'object') {
            setDateRange?.({ ...dateRange, to });
          } else {
            setDateRange?.({ ...dateRange, to: null });
          }
        }}
        value={dateRange.to ?? undefined}
        isValidDate={(date) => {
          if (validTypeTo === 'invalidAfterToday') {
            return isInvalidAfterToday(date);
          }
          if (validTypeTo === 'invalidFromBeforeDay') {
            return isInvalidFromBeforeDay(date);
          }
          return true;
        }}
        timeFormat="HH:mm:ss"
        viewDate={validViewDate()}
        {...restToProps}
      />
    </DatetimeWrapper>
  );
};

export default styled(DateTimeRange)``;
